import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, FileText, ExternalLink, BadgeCheck } from "lucide-react";
import { Section } from "@/components/Primitives";
import { useLang } from "@/context/LanguageContext";

export const CERTIFICATE_URL =
  "https://customer-assets-jt897jd0.emergentagent.net/job_work-travel-expert/artifacts/n4alx00z_115CC59E-7C1D-4372-98DB-A2DDBA7F7855.jpeg";
export const SUNBIZ_URL =
  "https://search.sunbiz.org/Inquiry/CorporationSearch/SearchResults?inquiryType=DocumentNumber&searchTerm=L26000334420";

export const TrustSection = ({ theme = "light" }) => {
  const { t } = useLang();
  const dark = theme === "dark";

  const facts = [
    { label: t("trust.docLabel"), value: "L26000334420" },
    { label: t("trust.statusLabel"), value: t("trust.statusValue") },
    { label: t("trust.stateLabel"), value: t("trust.stateValue") },
    { label: t("trust.issuerLabel"), value: t("trust.issuerValue") },
  ];

  return (
    <section data-testid="trust-section" className={dark ? "bg-navy" : "bg-white"}>
      <Section className="py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* TEXTE */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
            <span className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs uppercase tracking-[0.2em] ${dark ? "border-gold/40 bg-gold/10 text-gold" : "border-gold/50 bg-gold/10 text-gold"}`}>
              <ShieldCheck size={14} /> {t("trust.badge")}
            </span>
            <h2 data-testid="trust-title" className={`mt-5 font-heading text-3xl sm:text-4xl leading-tight ${dark ? "text-white" : "text-navy"}`}>
              <span className="mr-2">🇺🇸</span>{t("trust.title")}
            </h2>
            <div className="gold-line mt-5" />
            <div className={`mt-6 space-y-4 leading-relaxed text-sm sm:text-base ${dark ? "text-white/85" : "text-slate-700"}`}>
              <p>{t("trust.p1")}</p>
              <p>{t("trust.p2")}</p>
            </div>
            <p className={`mt-5 font-medium ${dark ? "text-gold" : "text-navy"}`}>{t("trust.p3")}</p>

            <dl className="mt-7 grid sm:grid-cols-2 gap-3">
              {facts.map((f) => (
                <div key={f.label} data-testid={`trust-fact-${f.label}`} className={`rounded-xl border px-4 py-3 ${dark ? "border-white/10 bg-white/[0.04]" : "border-border bg-[#F8F9FA]"}`}>
                  <dt className={`text-[11px] uppercase tracking-widest ${dark ? "text-white/50" : "text-muted-foreground"}`}>{f.label}</dt>
                  <dd className={`mt-1 text-sm font-semibold flex items-center gap-1.5 ${dark ? "text-white" : "text-navy"}`}>
                    {f.value === t("trust.statusValue") && <BadgeCheck size={15} className="text-gold" />}
                    {f.value}
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={CERTIFICATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="trust-certificate-btn"
                className="group inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-white px-6 py-3.5 font-medium transition-colors rounded-lg"
              >
                <FileText size={17} /> {t("trust.btnCert")}
              </a>
              <a
                href={SUNBIZ_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="trust-verify-btn"
                className={`group inline-flex items-center justify-center gap-2 px-6 py-3.5 font-medium rounded-lg border transition-colors ${dark ? "border-white/25 text-white hover:border-gold hover:text-gold" : "border-navy/20 text-navy hover:border-gold hover:text-gold"}`}
              >
                <ExternalLink size={16} /> {t("trust.btnVerify")}
              </a>
            </div>
          </motion.div>

          {/* CERTIFICAT */}
          <motion.a
            href={CERTIFICATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="trust-certificate-preview"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="group relative block mx-auto w-full max-w-md"
          >
            <div className="absolute -inset-3 rounded-2xl bg-gold/15 blur-2xl opacity-70 group-hover:opacity-100 transition-opacity" />
            <div className="relative rounded-xl overflow-hidden bg-white ring-1 ring-gold/40 shadow-2xl">
              <img
                src={CERTIFICATE_URL}
                alt={t("trust.caption")}
                loading="lazy"
                className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-[1.02]"
              />
            </div>
            <div className="mt-4 flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-gold">
              <ShieldCheck size={14} /> {t("trust.caption")}
            </div>
          </motion.a>
        </div>
      </Section>
    </section>
  );
};
